/**
 * Ammissibilità delle righe IVA sul canale fattura elettronica (SDI).
 *
 * Il canale scontrino NON ha più un predicato di ammissibilità proprio: la
 * classe RT è DERIVATA dalla faccia SdI tramite `projectRtClass`
 * (./rt-class). Qui resta solo la faccia fattura (scarto SDI 00445).
 */

import { POS_VAT_NATURE_CODES, isPosVatNatureCode, type PosVatNatureCode } from './natura-iva-map';

/** Esito di ammissibilità: escluso ⇒ motivo parlante per l'operatore. */
export type RtVatAdmissibility =
  | { readonly ammessa: true }
  | { readonly ammessa: false; readonly motivo: string };

// ---------------------------------------------------------------------------
// Canale fattura elettronica (SDI) — Task #3198
// ---------------------------------------------------------------------------

export const FE_MOTIVO_NATURA_INTERA =
  'codice natura generico N2/N3/N6: il tracciato fattura richiede il sotto-codice (scarto SDI 00445)';
export const FE_MOTIVO_NATURA_SCONOSCIUTA =
  'codice natura IVA non riconosciuto: non ammesso in fattura elettronica';

/** Nature INTERE non più ammesse dal tracciato FatturaPA (dal 01/01/2021). */
export const FE_NATURE_INTERE_VIETATE = ['N2', 'N3', 'N6'] as const;
export type FeNaturaIntera = (typeof FE_NATURE_INTERE_VIETATE)[number];

/** True se `natura` è un codice intero N2/N3/N6 (ammesso SOLO come aliquota di
 *  un reparto RT, mai su listini/prodotti/fatture). */
export function isNaturaIntera(natura: string | null | undefined): natura is FeNaturaIntera {
  return typeof natura === 'string' && (FE_NATURE_INTERE_VIETATE as readonly string[]).includes(natura);
}

/** Sotto-codici SdI di una natura intera (es. N2 → N2.1, N2.2). */
export function sottoCodiciDiNatura(natura: FeNaturaIntera): PosVatNatureCode[] {
  return POS_VAT_NATURE_CODES.filter((c) => c.startsWith(`${natura}.`));
}

/**
 * Messaggio operativo per il rifiuto di una natura intera su listino/prodotto/
 * fattura: "N2 è un codice intero non ammesso in fattura: usa N2.1 o N2.2".
 * Unica formulazione condivisa FE/BE (il BE la usa anche via API/import/merge).
 */
export function naturaInteraRejectionMessage(natura: FeNaturaIntera): string {
  const subs = sottoCodiciDiNatura(natura);
  const elenco = subs.length <= 2
    ? subs.join(' o ')
    : `${subs.slice(0, -1).join(', ')} o ${subs[subs.length - 1]}`;
  return `${natura} è un codice intero non ammesso in fattura: usa ${elenco}`;
}

/**
 * Ammissibilità per singolo codice natura sul canale fattura elettronica:
 * ammessi N1/N4/N5/N7 e tutti i sotto-codici (N2.x/N3.x/N6.x); vietati gli
 * interi N2/N3/N6 (controllo di scarto SDI 00445, specifiche FE v1.9+).
 * Parità 1:1 con la funzione DB `public.pos_vat_fe_allowed`.
 */
export function isNaturaAmmessaSuFE(natura: PosVatNatureCode): RtVatAdmissibility {
  if ((FE_NATURE_INTERE_VIETATE as readonly string[]).includes(natura)) {
    return { ammessa: false, motivo: FE_MOTIVO_NATURA_INTERA };
  }
  return { ammessa: true };
}

/**
 * Predicato principale canale fattura: una riga `pos_vat_rates` è ammessa
 * su SDI se imponibile (nessuna natura) oppure con natura diversa dagli
 * interi N2/N3/N6. Codici non riconosciuti → esclusi fail-closed.
 * Il riflesso persistito è la colonna GENERATED `pos_vat_rates.fe_allowed`.
 */
export function isVatRateAmmessaSuFE(row: {
  readonly is_taxable?: boolean | null;
  readonly nature_code?: string | null;
}): RtVatAdmissibility {
  const nature = row.nature_code ?? null;
  if (row.is_taxable === true || nature === null || nature === '') {
    return { ammessa: true };
  }
  if (!isPosVatNatureCode(nature)) {
    return { ammessa: false, motivo: FE_MOTIVO_NATURA_SCONOSCIUTA };
  }
  return isNaturaAmmessaSuFE(nature);
}
